/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */

import React from "react";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { formatContractDate, formatTerm, withFallback } from "kontrolEdge/helpers/formattersShappedData";
import { renderTags } from "kontrolEdge/helpers/renderTags";
import { ResourceTagsTableCell } from "../Shared/ResourceTagsTableCell";
import type { ResourceTagSummary } from "kontrolEdge/components/Shared/NameTagEdit/useResourceTags";
import { mapKeListResourceTags } from "../Shared/mapKeListResourceTags";
import {
    resolveServiceStatusTag,
    serviceStatusRank,
} from "kontrolEdge/helpers/serviceStatusTag";

export interface DataCenterConnection {
    id: number;
    customer?: {
      name: string;
      id: string;
      chargebeeCustomerId: string
    };
    name: string;
    tagsRich?: ResourceTagSummary[];
    renewDate: string;
    renewDateRaw: Date | null;
    mbpsNum: number;
    terms: string;
    currentStatus: string;
    internetCapacity: string;
    vlan: string;
    parentConnection?: {
      labelA?: string;
      idA?: number;
      labelB?: string;
      idB?: number;
      // Kind of the B endpoint so the list can route/label it correctly.
      // "port" (default) links to port-details; non-port B (SD-WAN hub /
      // FWAAS) is shown as plain text.
      kindB?: "port" | "sdwan_hub" | "fwaas";
    };
    parentConnectionId: string | number;
    subscriptionId: string;
    status: string;
  }  

  function toDateOrNull(s: unknown): Date | null {
    if (!s) return null;
    const d = new Date(String(s));
    return isNaN(d.getTime()) ? null : d;
  }
  function toNumberOrZero(x: unknown): number {
    if (typeof x === "number") return Number.isFinite(x) ? x : 0;
    const n = Number(String(x ?? "").replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
  }

export const datacenterColumns: GenericTableColumn<DataCenterConnection>[] = [
    {
    header: "DCI ID",
    accessor: "subscriptionId",
    },
    {
    header: "Parent Connection",
    accessor: "parentConnection",
    },
    {
      header: "Name",
      accessor: "name",
      renderCell: (row) => {
          const nameValue = row.name;
          // If it's null, empty string, or an empty array → just dash
          if (
          nameValue == null ||
          (typeof nameValue === "string" && nameValue.trim() === "") ||
          (Array.isArray(nameValue) && nameValue.length === 0)
          ) {
              return <span className="text-semantic-text-secondary">-</span>;
          }
          // Otherwise render GenericTag
          const label = Array.isArray(nameValue)
              ? nameValue.join(" ")
              : nameValue;
          return (
              <div className="flex items-center">
              <GenericTag variant="neutral" label={label} size="small"/>
              </div>
          );
      },
      },
    {
    header: "Mbps",
    accessor: "internetCapacity",
    },
    {
    header: "VLAN",
    accessor: "vlan",
    },
    {
      header: "Datacenter Status",
      renderCell: (row) => {
        const status = row.status;
        const { label, variant } = resolveServiceStatusTag(status);
        return <GenericTag variant={variant} label={label} size="small" />;
      },
      sortAccessor: (row) => serviceStatusRank(row.status),
    },
    {
    header: "Tags",
    renderCell: (row) => (
      <ResourceTagsTableCell
        tagsRich={row.tagsRich}
        maxVisible={1}
        triggerSingleRow
      />
    ),
    },
    {
    header: "Term",
    accessor: "terms",
    },
    {
    header: "Renew Date",
    accessor: "renewDate",
    },
];

// Resolve a DCI's B endpoint (port / SD-WAN hub / FWAAS) for list display.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const resolveRawBEndpoint = (
  c: any
): { label: string; id?: number; kind: "port" | "sdwan_hub" | "fwaas" } | null => {
  const svcLabel = (prefix: string, s: any) =>
    `${prefix} ${s?.name ?? s?.subscriptionId ?? s?.id ?? ""}`.trim();

  if (c.endpointBType === "sdwan_hub" && c.endpointBSdwanHub) {
    return { label: svcLabel("[SD-WAN Hub]", c.endpointBSdwanHub), id: c.endpointBSdwanHub.id, kind: "sdwan_hub" };
  }
  if (c.endpointBType === "fwaas" && c.endpointBFwaas) {
    return { label: svcLabel("[FWAAS]", c.endpointBFwaas), id: c.endpointBFwaas.id, kind: "fwaas" };
  }
  if (c.kontrolEdgePortB?.subid) {
    return { label: `[Port B] ${c.kontrolEdgePortB.subid}`, id: c.kontrolEdgePortB.id, kind: "port" };
  }
  // Fallbacks when the discriminator is missing but a service is present.
  if (c.endpointBSdwanHub) {
    return { label: svcLabel("[SD-WAN Hub]", c.endpointBSdwanHub), id: c.endpointBSdwanHub.id, kind: "sdwan_hub" };
  }
  if (c.endpointBFwaas) {
    return { label: svcLabel("[FWAAS]", c.endpointBFwaas), id: c.endpointBFwaas.id, kind: "fwaas" };
  }
  return null;
};

export const shapeDatacenterData = (connections: any[]): DataCenterConnection[] => {

    return connections.map((c) => {
      // Handle missing terms safely
      let parentConnection = "-";
      const bEndpoint = resolveRawBEndpoint(c);

      if (c.kontrolEdgeLagA?.subid && c.kontrolEdgeLagB?.subid) {
        parentConnection = `[LAG A] ${c.kontrolEdgeLagA.subid}, [LAG B] ${c.kontrolEdgeLagB.subid}`;
      } else if (c.kontrolEdgeLag?.subid) {
        parentConnection = `[LAG] ${c.kontrolEdgeLag.subid}`;
      } else if (c.kontrolEdgePortA?.subid && bEndpoint) {
        parentConnection = `[Port A] ${c.kontrolEdgePortA.subid}, ${bEndpoint.label}`;
      } else if (c.kontrolEdgePortA?.subid) {
        parentConnection = `[Port A] ${c.kontrolEdgePortA.subid}`;
      } else if (bEndpoint) {
        parentConnection = bEndpoint.label;
      } else if (c.parentConnection) {
        parentConnection = c.parentConnection;
      }

      const customer = c.customer
      ? {
        name: c.customer.name ?? "-",
        id: c.customer.id ?? "-",
        chargebeeCustomerId: c.customer.chargebeeCustomerId ?? "-"
        }
      : undefined;
      const renewDateRaw = toDateOrNull(c.renewDate);
      const mbpsNum = toNumberOrZero(c.mbps);
      const tagsRich = mapKeListResourceTags(c);
  
      return {
        id: c.id ?? 0,
        name: withFallback(c.name),
        customer,
        renewDateRaw,
        mbpsNum,
        renewDate: formatContractDate(c.renewDate),
        terms: formatTerm(c.term, c.term_unit),
        vlan: withFallback(c.vlan),
        currentStatus: withFallback(c.currentStatus),
        internetCapacity: withFallback(c.mbps),
        parentConnectionId: c.kontrolEdgePort?.id ?? "-",
        parentConnection: (() => {
          if (c.kontrolEdgeLagA || c.kontrolEdgeLagB) {
            return {
              labelA: c.kontrolEdgeLagA ? `[LAG A] ${c.kontrolEdgeLagA.subid}` : undefined,
              idA: c.kontrolEdgeLagA?.id,
              labelB: c.kontrolEdgeLagB ? `[LAG B] ${c.kontrolEdgeLagB.subid}` : undefined,
              idB: c.kontrolEdgeLagB?.id,
            };
          }
        
          if (c.kontrolEdgePortA || bEndpoint) {
            return {
              labelA: c.kontrolEdgePortA ? `[Port A] ${c.kontrolEdgePortA.subid}` : undefined,
              idA: c.kontrolEdgePortA?.id,
              labelB: bEndpoint?.label,
              idB: bEndpoint?.id,
              kindB: bEndpoint?.kind,
            };
          }
          return undefined;
        })(),        
        tagsRich,
        subscriptionId: withFallback(c.subscriptionId),
        status: withFallback(c.status),
      };
    });
  };
  
  
    